<template>
  <canvas
    ref="canvas"
    class="portal-particles"
    aria-hidden="true"
  />
</template>

<script>
import { reducedMotion, watchOnScreen, watchReducedMotion } from '@/motion.js'

// Nether portal particles drifting into whatever this is placed over.
// Coloured like Minecraft's PortalParticle: (0.9f, 0.3f, f) * brightness f.
const PX = 3

export default {
    props: {
        // "area": anywhere, pulled to the middle. "edge": around the rim, only drifting partway in.
        spawn: { type: String, default: 'area' },
    },
    data () {
        return { onScreen: false, reduced: reducedMotion() }
    },
    computed: {
        active () {
            return this.onScreen && !this.reduced
        },
    },
    watch: {
        active (on) {
            cancelAnimationFrame(this.handle)
            this.last = 0
            if (on) this.handle = requestAnimationFrame(this.frame)
        },
    },
    mounted () {
        this.particles = []
        this.width = 0
        this.height = 0
        this.last = 0
        this.resize()
        this.observer = new ResizeObserver(this.resize)
        this.observer.observe(this.$refs.canvas)
        this.unwatchScreen = watchOnScreen(this.$refs.canvas, visible => (this.onScreen = visible))
        this.unwatchMotion = watchReducedMotion(() => (this.reduced = reducedMotion()))
    },
    beforeUnmount () {
        cancelAnimationFrame(this.handle)
        this.observer.disconnect()
        this.unwatchScreen()
        this.unwatchMotion()
    },
    methods: {
        resize () {
            const el = this.$refs.canvas
            if (!el) return
            this.width = Math.max(1, Math.ceil(el.clientWidth / PX))
            this.height = Math.max(1, Math.ceil(el.clientHeight / PX))
            el.width = this.width
            el.height = this.height
        },
        // A random point on the rim of the canvas.
        rimPoint () {
            const { width, height } = this
            let p = Math.random() * 2 * (width + height)
            if (p < width) return [p, 0]
            if ((p -= width) < height) return [width, p]
            if ((p -= height) < width) return [width - p, height]
            return [0, height - (p - width)]
        },
        spawnParticle () {
            const { width, height } = this
            const f = Math.random() * 0.6 + 0.4
            let x0 = Math.random() * width
            let y0 = Math.random() * height
            let x1 = width * (0.25 + Math.random() * 0.5)
            let y1 = height * (0.3 + Math.random() * 0.4)
            if (this.spawn === 'edge') {
                [x0, y0] = this.rimPoint()
                const inward = 0.15 + Math.random() * 0.2
                x1 = x0 + (width / 2 - x0) * inward
                y1 = y0 + (height / 2 - y0) * inward
            }
            return {
                x0,
                y0,
                x1,
                y1,
                age: 0,
                life: 1.5 + Math.random() * 2.5,
                size: Math.random() < (this.spawn === 'edge' ? 0.5 : 0.3) ? 2 : 1,
                color: `rgb(${(f * 0.9 * 255) | 0},${(f * 0.3 * 255) | 0},${(f * 255) | 0})`,
                wobble: 2 + Math.random() * 6,
                phase: Math.random() * Math.PI * 2,
            }
        },
        frame (time) {
            this.handle = requestAnimationFrame(this.frame)
            const ctx = this.$refs.canvas && this.$refs.canvas.getContext('2d')
            const { width, height } = this
            if (!ctx || !width) return
            const dt = this.last ? Math.min(0.1, (time - this.last) / 1000) : 0
            this.last = time

            const target = Math.round(this.spawn === 'edge' ? (width + height) / 2.5 : (width * height) / 900)
            this.particles = this.particles.filter(p => (p.age += dt) < p.life)
            while (this.particles.length < target) this.particles.push(this.spawnParticle())

            ctx.clearRect(0, 0, width, height)
            for (const p of this.particles) {
                const t = p.age / p.life
                const pull = t * t
                const x = p.x0 + (p.x1 - p.x0) * pull + Math.sin(p.phase + t * 6) * p.wobble * (1 - t)
                const y = p.y0 + (p.y1 - p.y0) * pull + Math.cos(p.phase + t * 5) * p.wobble * 0.5 * (1 - t)
                ctx.globalAlpha = Math.min(1, t * 8, (1 - t) * 4)
                ctx.fillStyle = p.color
                ctx.fillRect(x | 0, y | 0, t > 0.7 ? 1 : p.size, t > 0.7 ? 1 : p.size)
            }
        },
    },
}
</script>

<style scoped>
.portal-particles {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  image-rendering: pixelated;
}
</style>
